import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styled from 'styled-components';
import { BlockColorEnum } from '../../enum/block-color.enum';
import { loop } from '../../util/number/loop';
import { limitValueToRange } from '../../util/number/limit-value-to-range.util';
import { mapToRange } from '../../util/number/map-to-range.util';
import { windowResize$ } from '../../util/dom/window-resize.util';
import { addEventListenerToObject } from '../../util/dom/add-event-listener-to-object.util';
import { action, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { ColorEnum } from '../../enum/color.enum';
import { lightenColor } from '../../util/style/lighten-color.util';
import { getEventX } from '../../util/dom/get-event-x.util';
import { IInjectedProps } from '../../interface/component/injected-props.interface';
import { isNumberString } from '../../util/number/is-number-string.util';

interface IGradingWrapperProps {
  selected: boolean;
  isInvalid: boolean;
}

const GradingWrapper = styled.div`
  display: flex;
  position: relative;
  border-radius: 10px;
  box-shadow: inset 0px 1px 3px rgba(0, 0, 0, 0.3);
  background: ${BlockColorEnum.InactiveColor};
  height: 30px;
  margin: ${(props: IGradingWrapperProps) => (!props.selected ? 2 : 0)}px;
  align-items: stretch;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  border: ${(props: IGradingWrapperProps) =>
    props.selected
      ? `2px solid ${lightenColor(BlockColorEnum.PrimaryButtonColor(props), -0.1)}`
      : props.isInvalid
        ? `2px solid ${BlockColorEnum.ErrorColor(props)}`
        : `0px solid ${ColorEnum.Transparent(props)}`};
`;

const GradingTile = styled.div`
  pointer-events: none;
  flex: 1 1 20px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${BlockColorEnum.SecondaryTextColor};
  background: ${(props: { selected: boolean; isNull: boolean }) =>
    props.selected
      ? props.isNull
        ? lightenColor(BlockColorEnum.InactiveColor(props), -0.3)
        : BlockColorEnum.PrimaryButtonColor(props)
      : ColorEnum.Transparent(props)};
`;

const KeyboardListener = styled.input`
  width: 0px;
  height: 0px;
  position: absolute;
  z-index: -1;
  outline: none;
  &:focus {
    outline: none;
  }
  pointer-events: none;
`;

interface IGradingProps {
  id?: string;
  name?: string;
  disabled: boolean;
  minValue: number;
  maxValue: number;
  nullValueMin: number;
  nullValueMax: number;
  value: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  isInvalid?: boolean;
}

@inject('state')
@observer
export class Grading extends React.Component<IGradingProps & IInjectedProps> {
  @observable selected: boolean = false;
  containerRect;
  track;
  resizeStream;
  mouseMoveEventListener;
  mouseEndEventListener;
  touchMoveEventListener;
  touchEndEventListener;
  keyboardInterval;
  keyboardListenerRef;

  handleEnd = (e) => {
    this.onChange(e);
    this.removeListeners();
  };

  removeListeners = () => {
    if (this.mouseMoveEventListener) {
      this.mouseMoveEventListener.remove();
      this.mouseMoveEventListener = undefined;
    }
    if (this.mouseEndEventListener) {
      this.mouseEndEventListener.remove();
      this.mouseEndEventListener = undefined;
    }
    if (this.touchMoveEventListener) {
      this.touchMoveEventListener.remove();
      this.touchMoveEventListener = undefined;
    }
    if (this.touchEndEventListener) {
      this.touchEndEventListener.remove();
      this.touchEndEventListener = undefined;
    }
  };

  handleDrag = (e) => {
    this.onChange(e);
    this.mouseEndEventListener = addEventListenerToObject(window, 'mouseup', this.handleEnd);
    this.touchEndEventListener = addEventListenerToObject(window, 'touchend', this.handleEnd);
    this.mouseMoveEventListener = addEventListenerToObject(window, 'mousemove', this.onChange);
    this.touchMoveEventListener = addEventListenerToObject(window, 'touchmove', this.onChange);
  };

  onChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const eventPageX = getEventX(e);
    const percentOfValue = limitValueToRange(
      (eventPageX - this.containerRect.left) / this.containerRect.width,
      0,
      1,
    );

    const value = mapToRange(percentOfValue, 0, 1, this.props.minValue, this.props.maxValue + 1);

    this.props.onChange(
      // If we pull the slider all the way to the right, the value will be `maxValue + 1`, so we set it to `maxValue` in that case
      Math.floor(value) > this.props.maxValue ? this.props.maxValue : Math.floor(value),
    );
    this.focusField();
  };

  focusField = () => {
    if (this.props.state.configuration.isMobile) {
      return;
    }
    const kl = ReactDOM.findDOMNode(this.keyboardListenerRef);
    kl.focus();
    kl.setSelectionRange(0, String(this.props.value).length);
  };

  componentWillMount() {
    this.resizeStream = windowResize$().subscribe(() => {
      if (this.track) {
        this.containerRect = ReactDOM.findDOMNode(this.track).getBoundingClientRect();
      }
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.cleaInterval();
    this.removeListeners();
    this.resizeStream.unsubscribe();
  }

  componentDidMount() {
    this.containerRect = ReactDOM.findDOMNode(this.track).getBoundingClientRect();
  }

  @action
  selectComponent = () => {
    this.selected = true;
  };

  @action
  unselectComponent = () => {
    this.selected = false;
    this.cleaInterval();
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  cleaInterval = () => {
    if (this.keyboardInterval) {
      clearInterval(this.keyboardInterval);
      this.keyboardInterval = null;
    }
  };

  onKeyDown = (event) => {
    if (this.keyboardInterval) {
      return;
    }
    const timeoutFunction = (nudgeFunction) => () => {
      nudgeFunction();
      console.log('nudged timeout');
      this.keyboardInterval = setTimeout(timeoutFunction(nudgeFunction), 150);
    };
    if (event.keyCode === 39) {
      // RightArrow
      this.keyboardInterval = setTimeout(timeoutFunction(this.nudgeRight), 150);
    } else if (event.keyCode === 37) {
      // LeftArrow
      this.keyboardInterval = setTimeout(timeoutFunction(this.nudgeLeft), 150);
    }
  };

  onKeyUp = (event) => {
    this.cleaInterval();
    if (event.keyCode === 39) {
      // RightArrow
      this.nudgeRight();
    } else if (event.keyCode === 37) {
      // LeftArrow
      this.nudgeLeft();
    }
  };

  nudgeRight = () => {
    if (this.props.value === undefined || this.props.value === null) {
      this.props.onChange(this.props.minValue);
      return;
    }
    let newValue = this.props.value + 1;
    if (newValue > this.props.maxValue) {
      newValue = this.props.maxValue;
    }
    if (newValue !== this.props.value) {
      this.props.onChange(newValue);
    }
  };

  nudgeLeft = () => {
    let newValue = this.props.value - 1;
    if (newValue < this.props.minValue) {
      newValue = this.props.minValue;
    }
    if (newValue !== this.props.value) {
      this.props.onChange(newValue);
    }
  };

  onInputChange = (e) => {
    let newValue;
    if (isNumberString(e.target.value)) {
      newValue = Number(e.target.value);
    } else {
      newValue = this.props.value;
    }
    newValue = limitValueToRange(newValue, this.props.minValue, this.props.maxValue);
    this.props.onChange(newValue);
    this.focusField();
  };

  componentDidUpdate() {
    if (this.selected) {
      this.focusField();
    }
  }

  render() {
    return (
      <GradingWrapper
        id={this.props.id}
        selected={this.selected}
        isInvalid={this.props.isInvalid}
        ref={(r) => (this.track = r)}
        onMouseDown={!this.props.disabled && this.handleDrag}
        onTouchStart={!this.props.disabled && this.handleDrag}
      >
        {!this.props.disabled && (
          <KeyboardListener
            ref={(k) => (this.keyboardListenerRef = k)}
            id={this.props.name}
            name={this.props.name}
            value={this.props.value || 0}
            type="tel"
            onChange={this.onInputChange}
            onKeyDown={this.onKeyDown}
            onKeyUp={this.onKeyUp}
            onFocus={this.selectComponent}
            onBlur={this.unselectComponent}
          />
        )}
        {loop(this.props.minValue, this.props.maxValue).map((value) => (
          <GradingTile
            key={value}
            selected={value <= this.props.value}
            isNull={
              this.props.nullValueMin <= this.props.value &&
              this.props.nullValueMax >= this.props.value
            }
          >
            {value}
          </GradingTile>
        ))}
      </GradingWrapper>
    );
  }
}
